import type {
  FullContest as APIFullContest,
  ContestType,
  UserContest,
} from '@betterpool/api-types/contests-service';

import { createQueryString } from '~/domains/common/utils/queryString';
import {
  COMBINATIONS_PAGE_SIZE,
  INFINITE_SCROLL_PAGE_SIZE,
} from '~/domains/contest/constants/query';
import parseTeamPicks from '~/domains/contest/domains/services/parsers/parseTeamPicks';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import type { EliminatedEntry } from '~/domains/entrants-manager/types/revivals.types';

import { contestsServiceInstance } from './contest.service.instance';
import type { ContestStatusFilter } from './enums/contest-status-filter.enum';
import type {
  TContestAvailabilityReportResponse,
  TContestPicksAvailableResponse,
  TEntryPicksAvailableResponse,
  TEntryPicksUnavailableResponse,
} from './interfaces/contest-picks-available';
import type { TContestPicksUnavailableResponse } from './interfaces/contest-picks-unavailable';
import type { FullContest } from './interfaces/full-contest.interface';
import type { Entrant, UserEntrant } from './interfaces/entrant.interface';
import type { CreatePicksPayload, CreatePicksQuery } from './interfaces/create-picks.interface';
import type { APITeamPicks, TeamPicks } from './interfaces/team-picks.interface';
import parseFullContest from './parsers/parseFullContest';
import type {
  CombinationEntriesResponse,
  ContestSlateCombinationsResponse,
  ContestStatisticsBySlate,
  TContestStatisticsResponse,
} from './interfaces/contest-statistics';
import type { CreateTiersPayload } from './interfaces/create-tiers.interface';
import type { CreatePlayerSurvivorPicksPayload } from './interfaces/create-player-survivor-picks.interface';
import type { APIPicksheet, Picksheet } from './interfaces/picksheet.interface';
import type { GetPlayersBySlateResponse, TSortDir, TSortOption } from './interfaces/players';
import parseContestPicksheet from './parsers/parseContestPicksheet';
import type { ContestPlayerSurvivorStatistics } from './interfaces/contest-player-survivor-statistics';
import type { CsvUploadTask } from './interfaces/picks-csv-upload.interface';
import type { BuybackRequest, BuybackResponse } from './interfaces/buyback.interface';
import type { PlayerSlateEntriesResponse } from './interfaces/player-slate-entries.interface';
import type { PropSlateEntriesResponse } from './interfaces/prop-slate-entries.interface';
import type { StandingsReport } from './interfaces/standings-report.interface';

// TODO: Infer correct FullContest by specific type
const getContest = async (contestId: string): Promise<FullContest> => {
  const response = await contestsServiceInstance.get<APIFullContest>(`/contests/${contestId}`);
  return parseFullContest(response.data);
};

const getContestEntries = async (
  contestId: string,
  limit = 100,
  offset = 0
): Promise<PaginatedData<Entrant>> => {
  const response = await contestsServiceInstance.get<PaginatedData<Entrant>>(
    `/contests/${contestId}/entries?limit=${limit}&offset=${offset}`
  );
  return response.data;
};

const getContestPicksheetBySlateId = async ({
  contestId,
  slateId,
  entryId,
  sort,
  showAllPicks,
}: {
  contestId: string;
  slateId: string;
  entryId?: string;
  sort?: 'seed' | 'startTime';
  showAllPicks?: boolean;
}): Promise<Picksheet> => {
  const query = createQueryString({ contestId, entryId, sort, showAllPicks });

  const response = await contestsServiceInstance.get<APIPicksheet>(
    `/slates/${slateId}/picksheets${query}`
  );

  return parseContestPicksheet(response.data);
};

const makeContestPicks = async (
  { contestId, entryId, slateId }: CreatePicksQuery,
  payload: CreatePicksPayload | CreateTiersPayload | CreatePlayerSurvivorPicksPayload
): Promise<void> => {
  await contestsServiceInstance.post(
    `/contests/${contestId}/entries/${entryId}/slates/${slateId}/picks`,
    payload
  );
};

const getBulkContestTeamPicks = async (payload: {
  entryIds: string[];
  includeEntries?: boolean;
  slateId?: string;
  slateIds?: string[];
  showAllPicks?: boolean;
  userId?: string;
}): Promise<TeamPicks> => {
  const response = await contestsServiceInstance.post<APITeamPicks>(`/team-picks`, payload);

  return parseTeamPicks(response.data);
};

const getContestStats = async ({
  contestId,
}: {
  contestId: string;
}): Promise<TContestStatisticsResponse> => {
  const response = await contestsServiceInstance.get<TContestStatisticsResponse>(
    `/contests/${contestId}/statistics`
  );

  return response.data;
};

const getContestStatsBySlate = async ({
  contestId,
  slateId,
}: {
  contestId: string;
  slateId: string;
}): Promise<ContestStatisticsBySlate> => {
  const response = await contestsServiceInstance.get<ContestStatisticsBySlate>(
    `/contests/${contestId}/slates/${slateId}/statistics`
  );

  return response.data;
};

const getContestSlateCombinations = async ({
  contestId,
  slateId,
  offset = 0,
  limit = COMBINATIONS_PAGE_SIZE,
}: {
  contestId: string;
  slateId: string;
  offset?: number;
  limit?: number;
}): Promise<ContestSlateCombinationsResponse> => {
  const response = await contestsServiceInstance.get<ContestSlateCombinationsResponse>(
    `/contests/${contestId}/slates/${slateId}/statistics/combinations${createQueryString({
      offset,
      limit,
    })}`
  );

  return response.data;
};

const getCombinationEntries = async ({
  contestId,
  slateId,
  teamIds,
  offset = 0,
  limit = INFINITE_SCROLL_PAGE_SIZE,
}: {
  contestId: string;
  slateId: string;
  teamIds: string[];
  offset?: number;
  limit?: number;
}): Promise<CombinationEntriesResponse> => {
  const response = await contestsServiceInstance.get<CombinationEntriesResponse>(
    `/contests/${contestId}/slates/${slateId}/statistics/combinations/entries${createQueryString({
      teamIds: teamIds.join(','),
      offset,
      limit,
    })}`
  );

  return response.data;
};

const downloadPicksCSVTemplate = async (contestId: string, slateId: string): Promise<Blob> => {
  const response = await contestsServiceInstance.get<Blob>(
    `/contest/${contestId}/slate/${slateId}/picks-csv/template`,
    {
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
        'Content-Type': 'text/csv',
        'Content-Disposition': 'attachment; filename="pick-template.csv"',
      },
    }
  );

  return response.data;
};

const getPicksCSVUploadTasks = async ({
  contestId,
  slateId,
  limit = 10,
  offset = 0,
}: {
  contestId: string;
  slateId: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedData<CsvUploadTask>> => {
  const response = await contestsServiceInstance.get<PaginatedData<CsvUploadTask>>(
    `/tasks/picks-csv-upload${createQueryString({ contestId, slateId, limit, offset })}`
  );
  return response.data;
};

const uploadPicksCSV = async (contestId: string, slateId: string, file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);

  await contestsServiceInstance.post(`/contest/${contestId}/slate/${slateId}/picks-csv`, formData, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'multipart/form-data',
    },
  });
};

const getContestPlayerSurvivorStatsBySlate = async ({
  contestId,
  slateId,
}: {
  contestId: string;
  slateId: string;
}): Promise<ContestPlayerSurvivorStatistics> => {
  const response = await contestsServiceInstance.get<ContestPlayerSurvivorStatistics>(
    `/contests/${contestId}/slates/${slateId}/player-survivor-statistics`
  );

  return response.data;
};

const getContestPicksAvailable = async ({
  contestId,
  limit = 20,
  offset = 0,
  teamIds = [],
}: {
  contestId: string;
  limit: number;
  offset: number;
  teamIds?: readonly string[];
}): Promise<TContestPicksAvailableResponse> => {
  const response = await contestsServiceInstance.get<TContestPicksAvailableResponse>(
    `/contests/${contestId}/picks/available${createQueryString({
      limit,
      offset,
      teamIds: teamIds.join(','),
    })}`
  );

  return response.data;
};

const getEntryPicksAvailable = async ({
  entryId,
  limit = 20,
  offset = 0,
}: {
  entryId: string;
  limit: number;
  offset: number;
}): Promise<TEntryPicksAvailableResponse> => {
  const response = await contestsServiceInstance.get<TEntryPicksAvailableResponse>(
    `/entries/${entryId}/picks/available${createQueryString({ limit, offset })}`
  );

  return response.data;
};

const getContestPicksUnavailable = async ({
  contestId,
  limit = 20,
  offset = 0,
  teamIds = [],
}: {
  contestId: string;
  limit: number;
  offset: number;
  teamIds?: readonly string[];
}): Promise<TContestPicksUnavailableResponse> => {
  const response = await contestsServiceInstance.get<TContestPicksUnavailableResponse>(
    `/contests/${contestId}/picks/unavailable${createQueryString({
      limit,
      offset,
      teamIds: teamIds.join(','),
    })}`
  );

  return response.data;
};

const getEntryPicksUnavailable = async ({
  entryId,
  limit = 20,
  offset = 0,
}: {
  entryId: string;
  limit: number;
  offset: number;
}): Promise<TEntryPicksUnavailableResponse> => {
  const response = await contestsServiceInstance.get<TEntryPicksUnavailableResponse>(
    `/entries/${entryId}/picks/unavailable${createQueryString({ limit, offset })}`
  );

  return response.data;
};

const getContestAvailibilityReport = async ({
  contestId,
  limit = 20,
  offset = 0,
}: {
  contestId: string;
  limit: number;
  offset: number;
}): Promise<TContestAvailabilityReportResponse> => {
  const response = await contestsServiceInstance.get<TContestAvailabilityReportResponse>(
    `/contest/${contestId}/picks/availability-report${createQueryString({ limit, offset })}`
  );

  return response.data;
};

export type TSortByOption = 'SLATE_START' | 'ENTERED_DATE' | 'ENTRY_FEE' | 'PRIZE_POOL';
export type TSortByDir = 'ASC' | 'DESC';

const getContestSummaries = async (
  userId: string,
  payload: {
    limit: number;
    offset: number;
    sort?: {
      option: TSortByOption;
      dir: TSortByDir;
    };
    filters: {
      leagues?: string[];
      status?: ContestStatusFilter;
      contestType?: ContestType;
      hasEntryFee?: boolean;
    };
  }
): Promise<PaginatedData<UserContest>> => {
  const response = await contestsServiceInstance.post<PaginatedData<UserContest>>(
    `/contests/users/${userId}/summaries`,
    payload
  );

  return response.data;
};

type LocationResponse = {
  country: string;
};

const getCurrentLocation = async (): Promise<LocationResponse> => {
  const response = await contestsServiceInstance.get<LocationResponse>('/locations/current');

  return response.data;
};

const getPlayersBySlate = async ({
  contestId,
  slateId,
  ...rest
}: {
  contestId: string;
  slateId: string;
  limit?: number;
  offset?: number;
  onlyUserEntries?: boolean;
  sort?: TSortOption;
  dir?: TSortDir;
  tierId?: number;
  rosterCategoryId?: string;
}): Promise<GetPlayersBySlateResponse> => {
  const response = await contestsServiceInstance.get<GetPlayersBySlateResponse>(
    `/contests/${contestId}/slates/${slateId}/players${createQueryString(rest)}`
  );

  return response.data;
};

const getContestEntrants = async ({
  contestId,
  limit = 20,
  offset = 0,
}: {
  contestId: string;
  limit: number;
  offset: number;
}): Promise<PaginatedData<UserEntrant>> => {
  const response = await contestsServiceInstance.get<PaginatedData<UserEntrant>>(
    `/contests/${contestId}/users`,
    {
      params: { limit, offset },
    }
  );

  return response.data;
};

const getContestSlates = async ({ contestId }: { contestId: string }): Promise<ContestSlate[]> => {
  const response = await contestsServiceInstance.get<{ data: ContestSlate[] }>(
    `/contests/${contestId}/slates`
  );

  return response.data?.data;
};

const getContestStandingsReport = async ({
  contestId,
}: {
  contestId: string;
}): Promise<StandingsReport> => {
  const response = await contestsServiceInstance.get<StandingsReport>(
    `/contests/${contestId}/reports/slate-standings`
  );

  return response.data;
};

const makeBuyback = async (payload: BuybackRequest): Promise<BuybackResponse> => {
  const response = await contestsServiceInstance.post<BuybackResponse>('/buybacks', payload);
  return response.data;
};

const getContestEliminatedEntries = async ({
  contestId,
  slateId,
  limit = 20,
  offset = 0,
  search,
  sort,
  sortDirection,
}: {
  contestId: string;
  slateId: string;
  limit?: number;
  offset?: number;
  search?: string;
  sort?: string;
  sortDirection?: 'ASC' | 'DESC';
}): Promise<PaginatedData<EliminatedEntry>> => {
  const queryParams: Record<string, number | string> = { limit, offset };

  if (search) {
    queryParams.search = search;
  }

  if (sort) {
    queryParams.sort = sort;
  }

  if (sortDirection) {
    queryParams.sortDirection = sortDirection;
  }

  const response = await contestsServiceInstance.get<PaginatedData<EliminatedEntry>>(
    `/contests/${contestId}/slates/${slateId}/eliminated${createQueryString(queryParams)}`
  );

  return response.data;
};

const commissionerReviveEntries = async ({
  contestId,
  entryIds,
}: {
  contestId: string;
  entryIds: string[];
}): Promise<void> => {
  await contestsServiceInstance.post('/buybacks/commissioner-revive', {
    contestId,
    entryIds,
  });
};

const getPlayerSlateEntries = async ({
  playerId,
  contestId,
  slateId,
  limit = 20,
  offset = 0,
}: {
  playerId: string;
  contestId: string;
  slateId: string;
  limit?: number;
  offset?: number;
}): Promise<PlayerSlateEntriesResponse> => {
  const response = await contestsServiceInstance.get<PlayerSlateEntriesResponse>(
    `/players/${playerId}/entries${createQueryString({
      contestId,
      slateId,
      limit,
      offset,
    })}`
  );
  return response.data;
};

const getPropSlateEntries = async ({
  propId,
  contestId,
  slateId,
  limit = 20,
  offset = 0,
}: {
  propId: string;
  contestId: string;
  slateId: string;
  limit?: number;
  offset?: number;
}): Promise<PropSlateEntriesResponse> => {
  const response = await contestsServiceInstance.get<PropSlateEntriesResponse>(
    `/props/${propId}/entries${createQueryString({
      contestId,
      slateId,
      limit,
      offset,
    })}`
  );
  return response.data;
};

export {
  getBulkContestTeamPicks,
  getCombinationEntries,
  getContest,
  getContestAvailibilityReport,
  getContestEntries,
  getContestPicksAvailable,
  getContestPicksheetBySlateId,
  getContestPicksUnavailable,
  getContestSlateCombinations,
  getContestStats,
  getContestStatsBySlate,
  getContestSummaries,
  getCurrentLocation,
  getEntryPicksAvailable,
  getEntryPicksUnavailable,
  getPlayersBySlate,
  getPlayerSlateEntries,
  getPropSlateEntries,
  makeContestPicks,
  getContestEntrants,
  getContestSlates,
  getContestStandingsReport,
  getContestEliminatedEntries,
  getContestPlayerSurvivorStatsBySlate,
  makeBuyback,
  commissionerReviveEntries,
  getPicksCSVUploadTasks,
  downloadPicksCSVTemplate,
  uploadPicksCSV,
};
